/**
 * Save-as-new-version dialog. Triggered from the detail page's
 * "Save" button in edit mode. Captures the commit message and an
 * optional "publish immediately" checkbox. Save with publish flips
 * the active pointer to the new version in one round trip
 * (createVersion → activate); save without publish leaves the
 * version as a draft that the operator can activate later from
 * the versions rail.
 */
import { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@/components/ui'

export interface SaveVersionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** What version number the new save will become. Just a UI hint
   * ("Will become v9"); the server is the source of truth and
   * assigns the actual number via $inc. */
  nextVersionPreview: number
  /** Returns commitMessage + publishImmediately so the parent can
   * choose whether to chain createVersion → activate or stop at
   * createVersion. The parent owns the actual API calls so it can
   * also do the post-save reload. */
  onSubmit: (input: { commitMessage: string; details?: string; publish: boolean }) => Promise<void>
  saving?: boolean
}

export function SaveVersionDialog({
  open,
  onOpenChange,
  nextVersionPreview,
  onSubmit,
  saving = false,
}: SaveVersionDialogProps) {
  const [commitMessage, setCommitMessage] = useState('')
  const [details, setDetails] = useState('')
  const [publish, setPublish] = useState(true)

  useEffect(() => {
    if (open) {
      setCommitMessage('')
      setDetails('')
      setPublish(true)
    }
  }, [open])

  const subject = commitMessage.trim()
  const canSubmit = subject.length > 0 && !saving

  async function handleSubmit(): Promise<void> {
    if (!canSubmit) return
    await onSubmit({
      commitMessage: subject,
      details: details.trim() || undefined,
      publish,
    })
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Save new version</DialogTitle>
          <DialogDescription>
            Will become v{nextVersionPreview}{publish ? ', and immediately become active.' : ' as a draft.'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="save-commit">
              Commit message <span className="text-destructive">*</span>
            </Label>
            <Input
              id="save-commit"
              value={commitMessage}
              onChange={(e) => setCommitMessage(e.target.value)}
              autoFocus
              placeholder="Short description of what changed"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault()
                  void handleSubmit()
                }
              }}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="save-details">Details (optional)</Label>
            <Textarea
              id="save-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Why the change, or additional context"
              rows={3}
            />
          </div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={publish}
              onChange={(e) => setPublish(e.target.checked)}
            />
            <span className="text-sm">Publish immediately (make this the active version)</span>
          </label>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {saving ? 'Saving…' : (publish ? 'Save & publish' : 'Save as draft')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
